import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UsersRound } from 'lucide-react';
import type { Grupo, GrupoDetalle, UsuarioAdmin } from '@doc/shared';
import { api } from '../../lib/api.js';
import { Cabecera } from '../../layout/Marco.js';
import { Confirmar, Modal } from '../../ui/Modal.js';
import { useAvisos } from '../../ui/avisos.js';

export function GruposPage() {
  const cliente = useQueryClient();
  const { avisar, avisarError } = useAvisos();
  const [editando, setEditando] = useState<string | 'nuevo' | null>(null);
  const [borrando, setBorrando] = useState<Grupo | null>(null);

  const { data: grupos, isLoading } = useQuery({ queryKey: ['grupos'], queryFn: () => api<Grupo[]>('/grupos') });

  const eliminar = useMutation({
    mutationFn: (id: string) => api(`/grupos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['grupos'] });
      avisar('Grupo eliminado');
    },
    onError: avisarError,
  });

  return (
    <>
      <Cabecera
        titulo="Grupos"
        subtitulo="Dar permisos a un grupo evita repasar carpeta por carpeta cuando entra alguien nuevo"
        acciones={
          <button className="btn btn--primario" onClick={() => setEditando('nuevo')}>
            <Plus size={14} /> Nuevo grupo
          </button>
        }
      />

      <div className="pagina">
        {isLoading && <div className="cargando">Cargando…</div>}

        {grupos && grupos.length === 0 && (
          <div className="vacio">
            <div className="icono">
              <UsersRound size={36} />
            </div>
            <p>Todavía no hay grupos.</p>
          </div>
        )}

        {grupos && grupos.length > 0 && (
          <table className="tabla">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Descripción</th>
                <th style={{ width: 110 }}>Miembros</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <UsersRound size={14} style={{ color: 'var(--green-light)' }} /> {g.nombre}
                    </div>
                  </td>
                  <td className="muted">{g.descripcion ?? '—'}</td>
                  <td>
                    <span className="badge">{g.miembros}</span>
                  </td>
                  <td style={{ textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn--sm" onClick={() => setEditando(g.id)}>
                      Editar
                    </button>
                    <button className="btn btn--sm btn--peligro" onClick={() => setBorrando(g)}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editando && <FormularioGrupo id={editando === 'nuevo' ? null : editando} alCerrar={() => setEditando(null)} />}

      {borrando && (
        <Confirmar
          titulo={`Eliminar "${borrando.nombre}"`}
          peligro
          etiquetaConfirmar="Eliminar grupo"
          mensaje={
            <>
              El grupo desaparece y con él <strong>todos los permisos que tenía sobre carpetas</strong>. Sus{' '}
              {borrando.miembros} miembro(s) perderán el acceso que les llegaba por esta vía.
            </>
          }
          alConfirmar={() => eliminar.mutate(borrando.id)}
          alCerrar={() => setBorrando(null)}
        />
      )}
    </>
  );
}

function FormularioGrupo({ id, alCerrar }: { id: string | null; alCerrar(): void }) {
  const cliente = useQueryClient();
  const { avisar, avisarError } = useAvisos();

  const { data: detalle } = useQuery({
    queryKey: ['grupo', id],
    queryFn: () => api<GrupoDetalle>(`/grupos/${id}`),
    enabled: !!id,
  });
  const { data: usuarios } = useQuery({ queryKey: ['usuarios'], queryFn: () => api<UsuarioAdmin[]>('/usuarios') });

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [miembros, setMiembros] = useState<Set<string> | null>(null);

  // El formulario se rellena cuando llega el detalle; antes de eso los campos
  // están vacíos porque todavía no hay nada que mostrar.
  useEffect(() => {
    if (!detalle || miembros !== null) return;
    setNombre(detalle.nombre);
    setDescripcion(detalle.descripcion ?? '');
    setMiembros(new Set(detalle.usuarios.map((u) => u.id)));
  }, [detalle, miembros]);

  const guardar = useMutation({
    mutationFn: async () => {
      const cuerpo = { nombre, descripcion: descripcion || null, usuarios: [...(miembros ?? [])] };
      if (id) return api(`/grupos/${id}`, { metodo: 'PATCH', cuerpo });
      const creado = await api<{ id: string }>('/grupos', { metodo: 'POST', cuerpo: { nombre, descripcion: descripcion || null } });
      if ((miembros?.size ?? 0) > 0) {
        await api(`/grupos/${creado.id}`, { metodo: 'PATCH', cuerpo: { usuarios: [...(miembros ?? [])] } });
      }
      return creado;
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['grupos'] });
      void cliente.invalidateQueries({ queryKey: ['grupo', id] });
      void cliente.invalidateQueries({ queryKey: ['sujetos'] });
      avisar('Grupo guardado');
      alCerrar();
    },
    onError: avisarError,
  });

  const alternar = (usuarioId: string) =>
    setMiembros((previo) => {
      const siguiente = new Set(previo ?? []);
      if (siguiente.has(usuarioId)) siguiente.delete(usuarioId);
      else siguiente.add(usuarioId);
      return siguiente;
    });

  return (
    <Modal
      titulo={id ? 'Editar grupo' : 'Nuevo grupo'}
      alCerrar={alCerrar}
      pie={
        <>
          <button className="btn" onClick={alCerrar}>
            Cancelar
          </button>
          <button className="btn btn--primario" onClick={() => guardar.mutate()} disabled={guardar.isPending || nombre.trim().length < 2}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="campo">
        <label>Nombre</label>
        <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
      </div>
      <div className="campo">
        <label>Descripción</label>
        <input className="input" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="campo">
        <label>Miembros</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
          {(usuarios ?? []).map((u) => (
            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                className="check"
                type="checkbox"
                checked={(miembros ?? new Set()).has(u.id)}
                onChange={() => alternar(u.id)}
              />
              {u.nombre} <span className="muted">· {u.email}</span>
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}
